import { z } from 'zod';
import {
  CompanyViewSchema,
  CustomerProfileViewSchema,
  HotelLoyaltyProgramsSchema,
  VoucherViewSchema,
} from '@voll-intelligence/domain';
import type { HttpService } from '@web/data/services';

/**
 * Consolidated customer dossier for `GET /orgs/:orgId/customers/:id/dossier`
 * (the P1 kanban bridge proxies it from customer-api). Post-KILL-02 the dossier no
 * longer embeds grouped trips — the `trips` sub-object carries only the flat
 * `inactiveVouchers` (CANCELLED/EXPIRED/DECLINED). Composes the importable domain
 * pieces (`CustomerProfileViewSchema`, `VoucherViewSchema`, `CompanyViewSchema`) —
 * reuse, don't redefine. Default `z.object` STRIP mode drops unknown keys, so an
 * upstream regression cannot surface raw tier-3 (`document`/`documents`) in the UI.
 * `hasNoCPF` is a SANCTIONED view key on `CustomerProfileViewSchema` (F1 perfil) —
 * it DOES reach the UI, unlike the raw documents.
 */
const CustomerDossierSchema = z.object({
  profile: CustomerProfileViewSchema,
  trips: z.object({
    inactiveVouchers: z.array(VoucherViewSchema),
  }),
  company: CompanyViewSchema.nullable(),
  // Deploy misto: customer-api pode ainda não estar servindo `hotelLoyalty` no
  // dossiê; `nullable().default(null)` mantém o contrato válido nesse meio-tempo.
  hotelLoyalty: HotelLoyaltyProgramsSchema.nullable().default(null),
});
// Não exportado de propósito: o consumidor deriva o tipo do repositório
// (`Awaited<ReturnType<typeof customerDossierRepository.getDossier>>`).
type CustomerDossier = z.infer<typeof CustomerDossierSchema>;

export interface CustomerDossierRepository {
  getDossier: (orgId: string, id: string) => Promise<CustomerDossier>;
}

export function createCustomerDossierRepository(http: HttpService): CustomerDossierRepository {
  return {
    getDossier: (orgId, id) =>
      http.get(`/orgs/${encodeURIComponent(orgId)}/customers/${encodeURIComponent(id)}/dossier`, {
        schema: CustomerDossierSchema,
      }),
  };
}
